import { Theme } from 'features/ui/types'
import { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { MeasureType } from 'enums'
import i18n from 'i18n'
import { BarMetricItemProps, DonutMetricItemProps, GaugeMetricItemProps } from '../charts'
import { isExceedPercentThreshold, isExceedDistanceThreshold, isOnlyOneNonZeroValue } from './break'
import { ChartConfig } from '../types'
import { percentFormatter, valueFormatter } from './format'
import { DOMAIN_COLOR_SETTINGS } from './colorSettings'
import { getDomainIcon } from './icon'
import { isValidNumber } from '../charts/share/utils'
import getCalcBreakProgress from './roundBarProgress'
import { isConvergedMedia } from './viewOption'
import { getGaugeAxisRange } from './axis'
import { isNetSentiment } from './category'

export const convertToDonutData = (
  chartConfig: ChartConfig<'Donut'>
): DonutMetricItemProps['data'] => {
  const isOnlyOneNoneZeroValue = isOnlyOneNonZeroValue(
    chartConfig.seriesData[0].data.map((item) => item.value as number)
  )
  switch (chartConfig.category) {
    case MeasureType.MENTIONS:
      return chartConfig.seriesData[0].data.map((d) => ({
        name: d.label,
        y: d.value,
        isBreak:
          d.label === i18n.t('neutral_other') &&
          !isOnlyOneNoneZeroValue &&
          isExceedPercentThreshold(d, chartConfig),
        chartBreakTooltipContent: i18n.t('mentionsBreakTooltip')
      }))
    case MeasureType.UNIQUE_AUTHORS:
      return chartConfig.seriesData[0].data.map((d) => ({
        name: d.label,
        y: d.value,
        isBreak:
          d.label === i18n.t('unknown') &&
          !isOnlyOneNoneZeroValue &&
          isExceedPercentThreshold(d, chartConfig),
        chartBreakTooltipContent: i18n.t('uniqueAuthorsBreakTooltip')
      }))
    case MeasureType.ENGAGEMENTS:
      return chartConfig.seriesData[0].data.map((d) => ({
        name: d.label,
        y: d.value
      }))
    default:
      return chartConfig.seriesData[0].data.map((d) => ({ name: d.label, y: d.value }))
  }
}

export const convertToGaugeData = (
  chartConfig: ChartConfig<'Gauge'>,
  viewOption: InsightViewOptionStrings
): GaugeMetricItemProps['data'] => {
  const { yAxisMin } = getGaugeAxisRange(chartConfig.axis, isNetSentiment(chartConfig))
  if (isConvergedMedia(viewOption)) {
    return (
      chartConfig.legendData?.data.map((d) => ({
        name: d.label,
        y: isValidNumber(d.value) ? d.value : yAxisMin
      })) || []
    )
  }
  return [
    {
      name: chartConfig.label || '',
      y: isValidNumber(chartConfig.value) ? chartConfig.value : yAxisMin
    }
  ]
}

export const convertToGaugeLegendData = (chartConfig: ChartConfig<'Gauge'>) =>
  chartConfig.legendData?.data.map((d) => ({ name: d.label, y: d.value }))

export const convertToRoundBarData = (
  chartConfig: ChartConfig<'RoundBar'>,
  theme: Theme
): BarMetricItemProps['data'] => {
  const values = [...chartConfig.seriesData]
    // TODO: merge displayValue into d.data?.[0].value ?
    .filter((d) => !d.displayValue)
    .map((d) => d.data?.[0].value)
    .filter(isValidNumber)

  const maxValue = Math.max(...values)

  const { isExceedThreshold, isInLargeCluster } = isExceedDistanceThreshold(values)

  let calcBreakProgress: ReturnType<typeof getCalcBreakProgress>
  if (isExceedThreshold) {
    calcBreakProgress = getCalcBreakProgress(chartConfig, isInLargeCluster!)
  }

  return chartConfig.seriesData.map((d) => {
    const value = d.data?.[0].value
    return {
      name: d.label || '',
      displayValue: d.displayValue || valueFormatter(value),
      icon: getDomainIcon(d.label, theme),
      progressBar: {
        progress: isValidNumber(value) ? (value / maxValue) * 100 : 0,
        startPoint: 0,
        ...(isExceedThreshold && {
          progress: calcBreakProgress(d),
          hasBreakSymbol: isInLargeCluster?.(value),
          breakSymbolTooltipContent: i18n.t('domainsBreakTooltip')
        }),
        colorSetting: DOMAIN_COLOR_SETTINGS[d.label || '']
      }
    }
  })
}

export const convertToScaleBarData = (
  chartConfig: ChartConfig<'ScaleBar'>,
  theme: Theme
): BarMetricItemProps['data'] =>
  chartConfig.seriesData.map((d) => {
    const value = d.data?.[0].value
    return {
      name: d.label || '',
      displayValue: d.displayValue || percentFormatter(value),
      icon: getDomainIcon(d.label, theme),
      progressBar: {
        startPoint: 50,
        progress: isValidNumber(value) ? value / 2 : 0,
        colorSetting: DOMAIN_COLOR_SETTINGS[d.label || '']
      }
    }
  })
